import { createApi } from '@reduxjs/toolkit/query/react';
import { ListOrganizationsRequest, Organization, SearchOrganizationRequest } from '../../models/organization';
import { axiosBaseQuery } from './axios-base-query';
import APIS from '../../utils/apiUrl';
import { SearchResponse } from '../../utils/types';

/**
 * A redux-toolkit query api for organizations. The toolkit manages refreshes, caching, cancellation, etc
 * via its generated react hooks (see the exports at the bottom of the file). The hooks return useful
 * helper states like loading, error, or data
 *
 * @see https://redux-toolkit.js.org/rtk-query/overview
 */
export const organizationApi = createApi({
  reducerPath: 'organizations',
  baseQuery: axiosBaseQuery({ baseUrl: APIS.PORTAL_API }),
  tagTypes: [],
  refetchOnMountOrArgChange: true,
  endpoints: builder => ({
    listOrganizationsV2: builder.query<Organization[], ListOrganizationsRequest>({
      query: params => ({ url: '/api/v2/organizations/list', method: 'get', params }),
    }),
    searchOrganizations: builder.query<SearchResponse<Organization>, SearchOrganizationRequest>({
      query: params => ({
        url: '/api/organizations/search',
        method: 'get',
        params: buildParams(params),
      }),
    }),
  }),
});

export const {
  /** a hook to list organizations that meet the given criteria, see {@link ListOrganizationsRequest} for parameters */
  useListOrganizationsV2Query,
  /** A hook to get paginated search results of organizations that meet the given criteria, see {@link SearchOrganizationRequest} for parameters. */
  useSearchOrganizationsQuery,
} = organizationApi;

const buildParams = (request: SearchOrganizationRequest): URLSearchParams => {
  const params: URLSearchParams = new URLSearchParams();
  params.append('pageNumber', request.pageNumber.toString());
  params.append('pageSize', request.pageSize.toString());
  if (request.searchTerm) {
    params.append('searchTerm', request.searchTerm);
  }
  if (request.statuses && request.statuses.length > 0) {
    request.statuses.forEach(status => params.append('statuses', status));
  }
  if (request.statuses && request.statuses.length > 0) {
    request.statuses.forEach(status => params.append('statuses', status ? status : 'undefined'));
  }
  if (request.type) {
    params.append('type', request.type);
  }
  if (request.sort) {
    params.append('sortProperty', request.sort.property);
    params.append('sortOrder', request.sort.order);
  }
  return params;
};
